import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { httpGet } from '@/api/httpClient';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { SuperAdminPageHelp } from '@/components/SuperAdmin/SuperAdminPageHelp';
import { DateTimePicker } from '@/components/DateTimePicker';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { projectService } from '@/services/projectService';
import { areaService } from '@/services/areaService';
import { useDebounce } from '@/hooks/useDebounce';
import { computeChartRange, sanitizeChartValue } from '@/utils/chartRanges';
import type { PaginatedResponse, Project } from '@/types';

interface Attribute {
  id: number;
  uuid: string;
  name: string;
  metric?: string;
  variant?: string | null;
  measurement_point_id?: number | null;
}

interface TimeseriesResponse {
  attribute_id: string;
  measurement: string;
  start: string;
  stop: string;
  query?: string;
  points: Array<{ t: string; v: number }>;
}

type TabId = 'timeseries' | 'topology' | 'heatmap';

type AreaNode = {
  id: number;
  name: string;
  code: string | null;
  number: string | null;
  parent: string | null;
  type: {
    id: number;
    title: string;
    code: string;
  };
};

type HeatmapCountsResponse = {
  platform_project_id: number;
  core_project_id: number;
  days: string[];
  series: Array<{ room_id: number; day: string; count: number }>;
  query?: string;
};

type HeatmapRoomRow = { roomId: number; roomName: string; apartmentId: number | null; counts: number[] };

type HeatmapRoomItem = { id: number; name: string; apartmentId: number | null };
type HeatmapApartmentItem = { id: number; name: string; number?: string | null; code?: string | null };

type AreasTreeResponse = {
  member: AreaNode[];
  totalItems: number;
};

type AreaAttributeItem = {
  id: number;
  uuid: string | null;
  name: string;
  attributeTypeCode: string | null;
  measurementPointId: number | null;
  device: { id: number; name: string | null; code: string | null } | null;
  box: { id: number; name: string | null } | null;
};

type AreaAttributesResponse = {
  projectId: number;
  areaId: number;
  items: AreaAttributeItem[];
  totalItems: number;
};

export function InfluxApiTestPage() {
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<TabId>('timeseries');

  // Inputs (timeseries)
  const [selectedAttributeUuid, setSelectedAttributeUuid] = useState<string>('');
  const [start, setStart] = useState<string>('');
  const [stop, setStop] = useState<string>('');
  const [limit, setLimit] = useState<number>(2000);

  // Topology tab
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [selectedApartmentId, setSelectedApartmentId] = useState<number | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [selectedRoomAttributeUuid, setSelectedRoomAttributeUuid] = useState<string | null>(null);
  const [selectedRoomAttributeLabel, setSelectedRoomAttributeLabel] = useState<string>('');
  const [selectedRoomAttributeTypeCode, setSelectedRoomAttributeTypeCode] = useState<string | null>(null);
  const [roomAttrStart, setRoomAttrStart] = useState<string>('');
  const [roomAttrStop, setRoomAttrStop] = useState<string>('');
  const [roomAttrLimit, setRoomAttrLimit] = useState<number>(2000);
  const [roomAttrHoverIdx, setRoomAttrHoverIdx] = useState<number | null>(null);

  // Room search (topology)
  const [roomSearch, setRoomSearch] = useState<string>('');
  const debouncedRoomSearch = useDebounce(roomSearch, 250);
  const [isRoomSearchOpen, setIsRoomSearchOpen] = useState<boolean>(false);

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Timeseries tab
  const [timeseriesDebugQuery, setTimeseriesDebugQuery] = useState<string | null>(null);
  const [timeseriesResult, setTimeseriesResult] = useState<TimeseriesResponse | null>(null);

  // Heatmap tab
  const [heatmapMetric, setHeatmapMetric] = useState<string>('');
  const [heatmapVariant, setHeatmapVariant] = useState<string>('');
  const [heatmapApartmentId, setHeatmapApartmentId] = useState<number | null>(null);
  const [heatmapApartmentQuery, setHeatmapApartmentQuery] = useState<string>('');
  const debouncedHeatmapApartmentQuery = useDebounce(heatmapApartmentQuery, 200);
  const [isHeatmapApartmentOpen, setIsHeatmapApartmentOpen] = useState<boolean>(false);
  const [heatmapRoomSearch, setHeatmapRoomSearch] = useState<string>('');
  const debouncedHeatmapRoomSearch = useDebounce(heatmapRoomSearch, 200);

  const heatmapColorClass = (count: number): string => {
    if (count === 0) return 'bg-rose-500/80 border-rose-600/40';
    if (count < 50) return 'bg-amber-400/80 border-amber-500/40';
    return 'bg-emerald-500/80 border-emerald-600/40';
  };

  // Persist selected project for topology + heatmap
  useEffect(() => {
    const saved = localStorage.getItem('influx_test_selected_project_id');
    if (saved && /^\d+$/.test(saved)) {
      setSelectedProjectId(Number(saved));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProjectId) localStorage.setItem('influx_test_selected_project_id', String(selectedProjectId));
  }, [selectedProjectId]);

  // Reset heatmap apartment filter on project change
  useEffect(() => {
    setHeatmapApartmentId(null);
    setHeatmapApartmentQuery('');
    setIsHeatmapApartmentOpen(false);
  }, [selectedProjectId]);

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const projectsQuery = useQuery({
    queryKey: ['projects', 'topology'],
    queryFn: () => projectService.getProjects({ page: 1, itemsPerPage: 200 }),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (selectedProjectId !== null) return;
    const list = (projectsQuery.data as PaginatedResponse<Project> | undefined)?.data ?? [];
    if (list.length > 0) {
      setSelectedProjectId(list[0].id);
    }
  }, [projectsQuery.data, selectedProjectId]);

  const adminAreasLightQuery = useQuery({
    queryKey: ['adminAreasLight', selectedProjectId],
    enabled: !!selectedProjectId && activeTab === 'topology',
    queryFn: () => areaService.getAdminAreasByProjectLight(selectedProjectId!),
    staleTime: 30_000,
  });

  const roomSearchResults = (() => {
    const term = debouncedRoomSearch.trim().toLowerCase();
    if (!term) return [];
    const areas = adminAreasLightQuery.data ?? [];
    const roomsOnly = areas.filter((a) => {
      const code = (a.type?.code ?? '').toUpperCase();
      const title = (a.type?.title ?? '').toUpperCase();
      return code === 'ROOM' || title === 'ROOM' || title.includes('CHAMBRE');
    });
    const ranked = roomsOnly
      .map((r) => {
        const hay = `${r.name ?? ''} ${r.number ?? ''} ${r.code ?? ''} ${r.id}`.toLowerCase();
        const score = hay.includes(term) ? (hay.startsWith(term) ? 2 : 1) : 0;
        return { r, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score || (a.r.name ?? '').localeCompare(b.r.name ?? '') || a.r.id - b.r.id)
      .slice(0, 20)
      .map((x) => x.r);
    return ranked;
  })();

  const resolveParentId = (parent: any): number | null => {
    if (!parent) return null;
    if (typeof parent === 'object' && typeof parent.id === 'number') return parent.id;
    if (typeof parent === 'string') {
      const m = parent.match(/\/(\d+)$/);
      if (m) return Number(m[1]);
    }
    if (typeof parent === 'number') return parent;
    return null;
  };

  const handleSelectRoomFromSearch = (roomId: number) => {
    const areas = adminAreasLightQuery.data ?? [];
    const byId = new Map<number, any>();
    for (const a of areas) byId.set(a.id, a);

    const room = byId.get(roomId);
    if (!room) {
      setSelectedRoomId(roomId);
      return;
    }

    const apartmentId = resolveParentId(room.parent);
    const apartment = apartmentId ? byId.get(apartmentId) : null;
    const floorOrBuildingId = apartment ? resolveParentId(apartment.parent) : null;
    const floorOrBuilding = floorOrBuildingId ? byId.get(floorOrBuildingId) : null;

    const typeCode = (floorOrBuilding?.type?.code ?? '').toUpperCase();
    if (typeCode === 'FLOOR') {
      setSelectedFloorId(floorOrBuildingId);
      const buildingId = floorOrBuilding ? resolveParentId(floorOrBuilding.parent) : null;
      if (buildingId) setSelectedBuildingId(buildingId);
    } else if (typeCode === 'BUILDING') {
      setSelectedBuildingId(floorOrBuildingId);
      setSelectedFloorId(null);
    }

    if (apartmentId) setSelectedApartmentId(apartmentId);
    setSelectedRoomId(roomId);
    setRoomSearch(room.name ?? '');
    setIsRoomSearchOpen(false);
  };

  // smalt_core attributes list (source of truth for Influx mapping)
  const coreAttributesQuery = useQuery({
    queryKey: ['smaltCore', 'projectAttributes', selectedProjectId],
    enabled: !!selectedProjectId && activeTab === 'timeseries',
    queryFn: async () => {
      const resp = await httpGet<{ items: Attribute[] }>(`/admin/smalt-core/projects/${selectedProjectId}/attributes`);
      return resp.items ?? [];
    },
    retry: false,
    staleTime: 30_000,
  });

  // Heatmap filter options from smalt_core attributes (avoid typos)
  const coreAttributesForHeatmapFiltersQuery = useQuery({
    queryKey: ['smaltCore', 'projectAttributes', 'heatmapFilters', selectedProjectId],
    enabled: !!selectedProjectId && activeTab === 'heatmap',
    queryFn: async () => {
      const resp = await httpGet<{ items: Attribute[] }>(`/admin/smalt-core/projects/${selectedProjectId}/attributes`);
      return resp.items ?? [];
    },
    retry: false,
    staleTime: 60_000,
  });

  const heatmapMetricOptions = (() => {
    const set = new Set<string>();
    for (const a of coreAttributesForHeatmapFiltersQuery.data ?? []) {
      const m = (a.metric ?? '').trim();
      if (m) set.add(m);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  })();

  const heatmapVariantOptions = (() => {
    const set = new Set<string>();
    const metricFilter = heatmapMetric.trim();
    for (const a of coreAttributesForHeatmapFiltersQuery.data ?? []) {
      const m = (a.metric ?? '').trim();
      if (metricFilter && m !== metricFilter) continue;
      const v = (a.variant ?? '').toString().trim();
      if (v) set.add(v);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  })();

  useEffect(() => {
    if (activeTab !== 'timeseries') return;
    if (selectedAttributeUuid) return;
    if (coreAttributesQuery.data && coreAttributesQuery.data.length > 0) {
      setSelectedAttributeUuid(coreAttributesQuery.data[0].uuid);
    }
  }, [activeTab, coreAttributesQuery.data, selectedAttributeUuid]);

  const allRoomsQuery = useQuery({
    queryKey: ['roomsAll', selectedProjectId],
    enabled: !!selectedProjectId && activeTab === 'heatmap',
    queryFn: async () => {
      // Use existing admin optimized endpoint: returns a flat list (cheap) for the whole project.
      const areas = await areaService.getAdminAreasByProjectLight(selectedProjectId!);

      const rooms: HeatmapRoomItem[] = areas
        .filter((a) => {
          const code = (a.type?.code ?? '').toUpperCase();
          const title = (a.type?.title ?? '').toUpperCase();
          return code === 'ROOM' || title === 'ROOM' || title.includes('CHAMBRE');
        })
        .map((a) => ({ id: a.id, name: a.name, apartmentId: resolveParentId((a as any).parent) }));

      const apartments: HeatmapApartmentItem[] = areas
        .filter((a) => {
          const code = (a.type?.code ?? '').toUpperCase();
          const title = (a.type?.title ?? '').toUpperCase();
          return code === 'APPARTEMENT' || title === 'APPARTEMENT';
        })
        .map((a) => ({
          id: a.id,
          name: a.name,
          number: (a as any).number ?? null,
          code: (a as any).code ?? null,
        }));

      rooms.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      apartments.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      return { rooms, apartments };
    },
    staleTime: 60_000,
  });

  const heatmapApartments = allRoomsQuery.data?.apartments ?? [];
  const heatmapApartmentResults = (() => {
    const term = debouncedHeatmapApartmentQuery.trim().toLowerCase();
    const base = heatmapApartments;
    if (!term) return base.slice(0, 20);
    return base
      .filter((a) => {
        const hay = `${a.name ?? ''} ${a.number ?? ''} ${a.code ?? ''} ${a.id}`.toLowerCase();
        return hay.includes(term);
      })
      .slice(0, 20);
  })();

  const roomsHeatmapQuery = useQuery({
    queryKey: ['roomsHeatmapAgg', selectedProjectId, heatmapMetric.trim(), heatmapVariant.trim()],
    enabled: !!selectedProjectId && activeTab === 'heatmap' && ((allRoomsQuery.data?.rooms?.length ?? 0) > 0),
    queryFn: async (): Promise<{ days: string[]; rooms: HeatmapRoomRow[]; query?: string }> => {
      const resp = await httpGet<HeatmapCountsResponse>('/influx/v1/rooms/daily-counts', {
        platform_project_id: selectedProjectId!,
        metric: heatmapMetric.trim() || undefined,
        variant: heatmapVariant.trim() || undefined,
      });

      const days = resp.days ?? [];
      const byRoomDay = new Map<string, number>();
      for (const s of resp.series ?? []) {
        byRoomDay.set(`${s.room_id}|${s.day}`, s.count ?? 0);
      }

      const baseRooms = allRoomsQuery.data?.rooms ?? [];
      const rooms = baseRooms.map((r) => ({
        roomId: r.id,
        roomName: r.name,
        apartmentId: r.apartmentId,
        counts: days.map((d) => byRoomDay.get(`${r.id}|${d}`) ?? 0),
      }));

      return { days, rooms, query: resp.query };
    },
    staleTime: 0,
  });

  // Reset chain when upper selection changes
  useEffect(() => {
    setSelectedBuildingId(null);
    setSelectedFloorId(null);
    setSelectedApartmentId(null);
    setSelectedRoomId(null);
  }, [selectedProjectId]);

  useEffect(() => {
    setSelectedFloorId(null);
    setSelectedApartmentId(null);
    setSelectedRoomId(null);
  }, [selectedBuildingId]);

  useEffect(() => {
    setSelectedApartmentId(null);
    setSelectedRoomId(null);
  }, [selectedFloorId]);

  useEffect(() => {
    setSelectedRoomId(null);
  }, [selectedApartmentId]);

  const rootAreasQuery = useQuery({
    queryKey: ['areasTree', selectedProjectId, 'root'],
    enabled: !!selectedProjectId,
    queryFn: () => httpGet<AreasTreeResponse>(`/projects/${selectedProjectId}/areas/tree`),
    staleTime: 30_000,
  });

  const buildings = (rootAreasQuery.data?.member ?? []).filter((a) => a.type.code === 'BUILDING');

  useEffect(() => {
    if (selectedBuildingId !== null) return;
    if (buildings.length > 0) {
      setSelectedBuildingId(buildings[0].id);
    }
  }, [buildings, selectedBuildingId]);

  const buildingChildrenQuery = useQuery({
    queryKey: ['areasTree', selectedProjectId, 'children', selectedBuildingId],
    enabled: !!selectedProjectId && !!selectedBuildingId,
    queryFn: () =>
      httpGet<AreasTreeResponse>(`/projects/${selectedProjectId}/areas/tree`, {
        parentId: selectedBuildingId ?? undefined,
      }),
    staleTime: 30_000,
  });

  const floors = (buildingChildrenQuery.data?.member ?? []).filter((a) => a.type.code === 'FLOOR');
  const hasFloors = floors.length > 0;

  useEffect(() => {
    if (!hasFloors) {
      if (selectedFloorId !== null) setSelectedFloorId(null);
      return;
    }
    if (selectedFloorId === null && floors.length > 0) {
      setSelectedFloorId(floors[0].id);
    }
  }, [hasFloors, floors, selectedFloorId]);

  const apartmentsQuery = useQuery({
    queryKey: ['areasTree', selectedProjectId, 'apartments', hasFloors ? selectedFloorId : selectedBuildingId],
    enabled: !!selectedProjectId && (!!selectedBuildingId && (!hasFloors || !!selectedFloorId)),
    queryFn: () => {
      const parentId = hasFloors ? selectedFloorId : selectedBuildingId;
      return httpGet<AreasTreeResponse>(`/projects/${selectedProjectId}/areas/tree`, {
        parentId: parentId ?? undefined,
      });
    },
    staleTime: 30_000,
  });

  const apartments = (apartmentsQuery.data?.member ?? []).filter((a) => a.type.code === 'APPARTEMENT');

  useEffect(() => {
    if (selectedApartmentId !== null) return;
    if (apartments.length > 0) {
      setSelectedApartmentId(apartments[0].id);
    }
  }, [apartments, selectedApartmentId]);

  const roomsQuery = useQuery({
    queryKey: ['areasTree', selectedProjectId, 'rooms', selectedApartmentId],
    enabled: !!selectedProjectId && !!selectedApartmentId,
    queryFn: () =>
      httpGet<AreasTreeResponse>(`/projects/${selectedProjectId}/areas/tree`, {
        parentId: selectedApartmentId ?? undefined,
      }),
    staleTime: 30_000,
  });

  const rooms = (roomsQuery.data?.member ?? []).filter((a) => a.type.code === 'ROOM');

  useEffect(() => {
    if (selectedRoomId !== null) return;
    if (rooms.length > 0) {
      setSelectedRoomId(rooms[0].id);
    }
  }, [rooms, selectedRoomId]);

  const roomAttributesQuery = useQuery({
    queryKey: ['areaAttributes', selectedProjectId, selectedRoomId],
    enabled: !!selectedProjectId && !!selectedRoomId,
    queryFn: () =>
      httpGet<AreaAttributesResponse>(`/admin/smalt-core/projects/${selectedProjectId}/rooms/${selectedRoomId}/attributes`),
    staleTime: 10_000,
  });

  // Default chart date range (48h) when selecting an attribute
  useEffect(() => {
    if (!selectedRoomAttributeUuid) return;
    if (roomAttrStart && roomAttrStop) return;

    const now = new Date();
    const from = new Date(now.getTime() - 48 * 60 * 60 * 1000);
    const formatLocal = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    setRoomAttrStart(formatLocal(from));
    setRoomAttrStop(formatLocal(now));
  }, [selectedRoomAttributeUuid, roomAttrStart, roomAttrStop]);

  const roomAttrStartISO = roomAttrStart ? new Date(roomAttrStart).toISOString() : null;
  const roomAttrStopISO = roomAttrStop ? new Date(roomAttrStop).toISOString() : null;
  const roomAttrDateError =
    roomAttrStart && roomAttrStop && new Date(roomAttrStop) <= new Date(roomAttrStart)
      ? 'La date de début doit être antérieure à la date de fin'
      : null;

  const roomAttrTimeseriesQuery = useQuery({
    queryKey: ['influxV1Timeseries', selectedRoomAttributeUuid, roomAttrStartISO, roomAttrStopISO, roomAttrLimit],
    enabled: !!selectedRoomAttributeUuid && !!roomAttrStartISO && !!roomAttrStopISO && !roomAttrDateError,
    queryFn: () =>
      httpGet<TimeseriesResponse>('/influx/v1/timeseries', {
        attribute_uuid: selectedRoomAttributeUuid ?? undefined,
        start: roomAttrStartISO ?? undefined,
        stop: roomAttrStopISO ?? undefined,
        limit: String(roomAttrLimit),
      }),
    staleTime: 0,
  });

  const handleRunTimeseries = async () => {
    // Validation
    if (!selectedAttributeUuid || !start || !stop) {
      setError('Please fill all required fields');
      return;
    }

    const startDate = new Date(start);
    const stopDate = new Date(stop);

    if (stopDate <= startDate) {
      setError('Stop time must be greater than start time');
      return;
    }

    if (limit > 50000) {
      setError('Limit cannot exceed 50000');
      return;
    }

    setLoading(true);
    setError(null);
    setTimeseriesResult(null);
    setTimeseriesDebugQuery(null);

    try {
      // Convert datetime-local to RFC3339 UTC
      const startRFC3339 = startDate.toISOString();
      const stopRFC3339 = stopDate.toISOString();

      const data = await httpGet<TimeseriesResponse>('/influx/v1/timeseries', {
        attribute_uuid: selectedAttributeUuid,
        start: startRFC3339,
        stop: stopRFC3339,
        limit: String(limit),
      });

      if (data.query) {
        setTimeseriesDebugQuery(data.query);
      }
      setTimeseriesResult(data);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'status' in err && 'body' in err) {
        const httpErr = err as { status?: number; body?: any };
        
        if (httpErr.body && typeof httpErr.body === 'object' && 'query' in httpErr.body) {
          setTimeseriesDebugQuery(httpErr.body.query);
        }

        setError(
          `HTTP ${httpErr.status}: ${JSON.stringify({ ...httpErr.body, query: undefined }, null, 2)}`
        );
      } else {
        setError('Failed to fetch timeseries: ' + (err instanceof Error ? err.message : String(err)));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyJson = () => {
    if (timeseriesResult) {
      const jsonStr = JSON.stringify(timeseriesResult, null, 2);
      navigator.clipboard.writeText(jsonStr).then(() => {
        alert('JSON copied to clipboard!');
      }).catch(() => {
        alert('Failed to copy to clipboard');
      });
    }
  };

  // Set default dates (24h ago to now)
  useEffect(() => {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    // Format as datetime-local (YYYY-MM-DDTHH:mm)
    const formatLocal = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    if (!start) setStart(formatLocal(yesterday));
    if (!stop) setStop(formatLocal(now));
  }, []);

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'timeseries', label: 'Time series' },
    { id: 'topology', label: 'Topologie' },
    { id: 'heatmap', label: 'Room Data Heatmap' },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Super utilisateur</span> / InfluxDB API Test
          </div>
          <div className="flex items-center gap-4">
            <SuperAdminPageHelp path="/admin/influxdb-test" />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              <span className="material-icons-outlined">{isDark ? 'light_mode' : 'dark_mode'}</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <span className="material-icons-outlined text-lg">person</span>
              <span>{user?.fullName || 'Admin'}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">InfluxDB API Test</h1>

            <div className="bg-white dark:bg-[#16202A] rounded-lg shadow border border-slate-200 dark:border-[#1E293B] p-6 mb-6">
            <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
              {tabs.map((t) => {
                const isActive = t.id === activeTab;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setActiveTab(t.id);
                      setError(null);
                    }}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-primary/10 text-primary dark:text-accent'
                        : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {activeTab === 'timeseries' && (
              <div className="mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">Projet *</label>
                    <select
                      value={selectedProjectId ?? ''}
                      onChange={(e) => {
                        setSelectedProjectId(Number(e.target.value) || null);
                        setSelectedAttributeUuid('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                      required
                    >
                      {(projectsQuery.data?.data ?? []).length === 0 && <option value="">Aucun projet</option>}
                      {(projectsQuery.data?.data ?? []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">Attribute (smalt_core) *</label>
                <select
                  value={selectedAttributeUuid}
                  onChange={(e) => setSelectedAttributeUuid(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                  required
                >
                  <option value="">Select an attribute</option>
                  {(coreAttributesQuery.data ?? []).map((attr) => (
                    <option key={attr.id} value={attr.uuid}>
                      {attr.name} (UUID: {attr.uuid?.substring(0, 8)}...)
                    </option>
                  ))}
                </select>
                {coreAttributesQuery.isLoading && (
                  <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">Chargement des attributes smalt_core…</div>
                )}
                {coreAttributesQuery.isError && (
                  <div className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                    Impossible de charger la liste des attributes.
                  </div>
                )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'timeseries' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleRunTimeseries();
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <DateTimePicker value={start} onChange={setStart} label="Début *" />
                  </div>

                  <div>
                    <DateTimePicker value={stop} onChange={setStop} label="Fin *" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                      Limit (max 50000)
                    </label>
                    <input
                      type="number"
                      value={limit}
                      onChange={(e) => setLimit(Number(e.target.value))}
                      min="1"
                      max="50000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Running...' : 'Run'}
                  </button>
                  {timeseriesResult && (
                    <button
                      type="button"
                      onClick={handleCopyJson}
                      className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    >
                      Copy JSON
                    </button>
                  )}
                </div>
              </form>
            )}

            {activeTab === 'topology' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">Projet *</label>
                    <select
                      value={selectedProjectId ?? ''}
                      onChange={(e) => setSelectedProjectId(Number(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                      required
                    >
                      {(projectsQuery.data?.data ?? []).length === 0 && <option value="">Aucun projet</option>}
                      {(projectsQuery.data?.data ?? []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title}
                        </option>
                      ))}
                    </select>
                    {projectsQuery.isLoading && (
                      <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">Chargement des projets…</div>
                    )}
                    {projectsQuery.isError && (
                      <div className="mt-2 text-xs text-rose-600 dark:text-rose-400">Impossible de charger les projets.</div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">Bâtiment *</label>
                    <select
                      value={selectedBuildingId ?? ''}
                      onChange={(e) => setSelectedBuildingId(Number(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                      disabled={!selectedProjectId || rootAreasQuery.isLoading}
                      required
                    >
                      {buildings.length === 0 && <option value="">Aucun bâtiment</option>}
                      {buildings.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                    {rootAreasQuery.isLoading && (
                      <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">Chargement…</div>
                    )}
                    {rootAreasQuery.isError && (
                      <div className="mt-2 text-xs text-rose-600 dark:text-rose-400">Impossible de charger les bâtiments.</div>
                    )}
                  </div>

                  {hasFloors && (
                    <div>
                      <label className="block text-sm font-medium mb-2 dark:text-gray-300">Étage *</label>
                      <select
                        value={selectedFloorId ?? ''}
                        onChange={(e) => setSelectedFloorId(Number(e.target.value) || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                        disabled={!selectedBuildingId || buildingChildrenQuery.isLoading}
                        required
                      >
                        {floors.length === 0 && <option value="">Aucun étage</option>}
                        {floors.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">Appartement *</label>
                    <select
                      value={selectedApartmentId ?? ''}
                      onChange={(e) => setSelectedApartmentId(Number(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                      disabled={!selectedBuildingId || apartmentsQuery.isLoading || (hasFloors && !selectedFloorId)}
                      required
                    >
                      {apartments.length === 0 && <option value="">Aucun appartement</option>}
                      {apartments.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">Chambre *</label>
                    <select
                      value={selectedRoomId ?? ''}
                      onChange={(e) => setSelectedRoomId(Number(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                      disabled={!selectedApartmentId || roomsQuery.isLoading}
                      required
                    >
                      {rooms.length === 0 && <option value="">Aucune chambre</option>}
                      {rooms.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Rechercher une chambre</label>
                  <input
                    value={roomSearch}
                    onChange={(e) => {
                      setRoomSearch(e.target.value);
                      setIsRoomSearchOpen(true);
                    }}
                    onFocus={() => setIsRoomSearchOpen(true)}
                    placeholder="Nom, numéro, code, id…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                  />

                  {isRoomSearchOpen && debouncedRoomSearch.trim() !== '' && (
                    <div className="absolute z-20 mt-2 w-full bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-lg shadow-lg overflow-hidden">
                      {adminAreasLightQuery.isLoading && (
                        <div className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">Chargement…</div>
                      )}
                      {adminAreasLightQuery.isError && (
                        <div className="px-3 py-2 text-sm text-rose-600 dark:text-rose-400">
                          Impossible de charger les rooms (endpoint admin light).
                        </div>
                      )}

                      {!adminAreasLightQuery.isLoading && roomSearchResults.length === 0 && (
                        <div className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">Aucun résultat.</div>
                      )}

                      {roomSearchResults.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => handleSelectRoomFromSearch(r.id)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-colors"
                        >
                          <div className="text-sm font-medium text-slate-900 dark:text-white">
                            {r.name}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {r.number ? `N° ${r.number} · ` : ''}
                            {r.code ? `Code ${r.code}` : ''}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 dark:bg-[#1E293B] border border-slate-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">Attributes de la chambre</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {roomAttributesQuery.data ? `${roomAttributesQuery.data.totalItems} item(s)` : '—'}
                    </div>
                  </div>

                  {roomAttributesQuery.isLoading && (
                    <div className="text-sm text-slate-600 dark:text-slate-300">Chargement des attributes…</div>
                  )}
                  {roomAttributesQuery.isError && (
                    <div className="text-sm text-rose-600 dark:text-rose-400">
                      Impossible de charger les attributes pour cette chambre.
                    </div>
                  )}

                  {roomAttributesQuery.data && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                        <thead>
                          <tr className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            <th className="px-3 py-2">ID</th>
                            <th className="px-3 py-2">Nom</th>
                            <th className="px-3 py-2">Type</th>
                            <th className="px-3 py-2">UUID</th>
                            <th className="px-3 py-2">Device</th>
                            <th className="px-3 py-2">MeasurementPoint</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                          {roomAttributesQuery.data.items.map((it) => (
                            <tr
                              key={it.id}
                              className={`hover:bg-white/60 dark:hover:bg-slate-900/30 ${
                                it.uuid ? 'cursor-pointer' : 'opacity-60'
                              } ${selectedRoomAttributeUuid && it.uuid === selectedRoomAttributeUuid ? 'bg-white/60 dark:bg-slate-900/30' : ''}`}
                              onClick={() => {
                                if (!it.uuid) return;
                                setSelectedRoomAttributeUuid(it.uuid);
                                setSelectedRoomAttributeLabel(it.name);
                                setSelectedRoomAttributeTypeCode(it.attributeTypeCode);
                                // Reset dates to apply default 48h range
                                setRoomAttrStart('');
                                setRoomAttrStop('');
                              }}
                            >
                              <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{it.id}</td>
                              <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{it.name}</td>
                              <td className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">{it.attributeTypeCode ?? '—'}</td>
                              <td className="px-3 py-2 text-xs font-mono text-slate-600 dark:text-slate-300">
                                {it.uuid ? `${it.uuid.substring(0, 8)}…` : '—'}
                              </td>
                              <td className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">
                                {it.device ? `${it.device.name ?? 'Device'}` : '—'}
                              </td>
                              <td className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">
                                {it.measurementPointId ?? '—'}
                              </td>
                            </tr>
                          ))}
                          {roomAttributesQuery.data.items.length === 0 && (
                            <tr>
                              <td colSpan={6} className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">
                                Aucun attribute pour cette chambre.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {selectedRoomAttributeUuid && (
                  <div className="bg-white dark:bg-[#16202A] rounded-lg shadow border border-slate-200 dark:border-[#1E293B] p-4">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900 dark:text-white">Graphe</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{selectedRoomAttributeLabel}</div>
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        UUID: <span className="font-mono">{selectedRoomAttributeUuid.substring(0, 8)}…</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <DateTimePicker value={roomAttrStart} onChange={setRoomAttrStart} label="Début" />
                      </div>
                      <div>
                        <DateTimePicker value={roomAttrStop} onChange={setRoomAttrStop} label="Fin" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 dark:text-gray-300">Limit</label>
                        <input
                          type="number"
                          value={roomAttrLimit}
                          onChange={(e) => setRoomAttrLimit(Number(e.target.value))}
                          min="1"
                          max="50000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                        />
                      </div>
                    </div>

                    {roomAttrDateError && (
                      <div className="mb-3 text-sm text-rose-600 dark:text-rose-400">{roomAttrDateError}</div>
                    )}

                    {roomAttrTimeseriesQuery.isLoading && (
                      <div className="text-sm text-slate-600 dark:text-slate-300">Chargement des points…</div>
                    )}
                    {roomAttrTimeseriesQuery.isError && (
                      <div className="text-sm text-rose-600 dark:text-rose-400">
                        Erreur lors du chargement des points.
                      </div>
                    )}

                    {roomAttrTimeseriesQuery.data && (
                      <div className="bg-slate-50 dark:bg-[#1E293B] border border-slate-200 dark:border-gray-700 rounded-lg p-3 overflow-x-auto">
                        {roomAttrTimeseriesQuery.data.points?.length ? (
                          (() => {
                            const pts = roomAttrTimeseriesQuery.data.points.map((p) => ({
                              t: new Date(p.t).getTime(),
                              v: sanitizeChartValue(Number(p.v), selectedRoomAttributeLabel, null, selectedRoomAttributeTypeCode),
                            }));
                            const values = pts.map((p) => p.v);
                            const { min: minV, max: maxV, range } = computeChartRange(
                              values,
                              selectedRoomAttributeLabel,
                              null,
                              selectedRoomAttributeTypeCode
                            );
                            const minT = Math.min(...pts.map((p) => p.t));
                            const maxT = Math.max(...pts.map((p) => p.t));
                            const w = 900;
                            const h = 260;
                            const pad = 30;
                            const leftPad = 60;
                            const innerW = w - leftPad - pad;
                            const innerH = h - 2 * pad;
                            const getX = (i: number) => leftPad + (i / (pts.length - 1 || 1)) * innerW;
                            const getY = (v: number) => {
                              const clamped = Math.max(minV, Math.min(maxV, v));
                              return h - pad - ((clamped - minV) / range) * innerH;
                            };
                            const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(p.v)}`).join(' ');

                            const yTicks = 4;
                            const xTicks = 4;
                            const formatY = (v: number) => {
                              if (!Number.isFinite(v)) return '';
                              return (Math.round(v * 10) / 10).toString();
                            };
                            const formatX = (ms: number) => {
                              const dt = new Date(ms);
                              // ex: 14/02 13:40
                              return dt.toLocaleString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              });
                            };

                            const hoverI = roomAttrHoverIdx !== null ? Math.max(0, Math.min(pts.length - 1, roomAttrHoverIdx)) : null;
                            const hoverPt = hoverI !== null ? pts[hoverI] : null;
                            const hoverX = hoverI !== null ? getX(hoverI) : null;
                            const hoverY = hoverPt ? getY(hoverPt.v) : null;
                            const hoverLabelX = hoverPt ? formatX(hoverPt.t) : '';
                            const hoverLabelY =
                              hoverPt && Number.isFinite(hoverPt.v)
                                ? (() => {
                                    const v = hoverPt.v;
                                    const outOfRange = v < minV || v > maxV;
                                    return (Math.round(v * 100) / 100).toString() + (outOfRange ? ' (hors plage)' : '');
                                  })()
                                : '';

                            return (
                              <svg
                                viewBox={`0 0 ${w} ${h}`}
                                className="w-full h-auto min-w-[900px]"
                                onMouseLeave={() => setRoomAttrHoverIdx(null)}
                                onMouseMove={(e) => {
                                  const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
                                  if (rect.width <= 0) return;
                                  const x = ((e.clientX - rect.left) / rect.width) * w;
                                  if (x < leftPad || x > w - pad) {
                                    setRoomAttrHoverIdx(null);
                                    return;
                                  }
                                  const ratio = (x - leftPad) / innerW;
                                  const idx = Math.round(ratio * (pts.length - 1));
                                  setRoomAttrHoverIdx(Math.max(0, Math.min(pts.length - 1, idx)));
                                }}
                              >
                                {/* Grid + Y ticks */}
                                {Array.from({ length: yTicks + 1 }).map((_, i) => {
                                  const vv = minV + (i / yTicks) * range;
                                  const y = getY(vv);
                                  return (
                                    <g key={`y-${i}`}>
                                      <line
                                        x1={leftPad}
                                        x2={w - pad}
                                        y1={y}
                                        y2={y}
                                        stroke="rgba(148,163,184,0.25)"
                                        strokeWidth="1"
                                      />
                                      <text
                                        x={leftPad - 8}
                                        y={y + 4}
                                        fontSize="10"
                                        fill="rgba(148,163,184,0.9)"
                                        textAnchor="end"
                                      >
                                        {formatY(vv)}
                                      </text>
                                    </g>
                                  );
                                })}

                                {/* Grid + X ticks */}
                                {Array.from({ length: xTicks + 1 }).map((_, i) => {
                                  const x = leftPad + (i / xTicks) * innerW;
                                  const ms = minT + (i / xTicks) * (maxT - minT || 1);
                                  return (
                                    <g key={`x-${i}`}>
                                      <line
                                        x1={x}
                                        x2={x}
                                        y1={pad}
                                        y2={h - pad}
                                        stroke="rgba(148,163,184,0.18)"
                                        strokeWidth="1"
                                      />
                                      <text
                                        x={x}
                                        y={h - 10}
                                        fontSize="10"
                                        fill="rgba(148,163,184,0.9)"
                                        textAnchor="middle"
                                      >
                                        {formatX(ms)}
                                      </text>
                                    </g>
                                  );
                                })}

                                {/* Axes */}
                                <line x1={leftPad} x2={leftPad} y1={pad} y2={h - pad} stroke="rgba(148,163,184,0.7)" strokeWidth="1.2" />
                                <line x1={leftPad} x2={w - pad} y1={h - pad} y2={h - pad} stroke="rgba(148,163,184,0.7)" strokeWidth="1.2" />

                                {/* Axis labels */}
                                <text x={(leftPad + (w - pad)) / 2} y={h - 2} fontSize="11" fill="rgba(148,163,184,0.9)" textAnchor="middle">
                                  Temps
                                </text>
                                <text
                                  x={14}
                                  y={(pad + (h - pad)) / 2}
                                  fontSize="11"
                                  fill="rgba(148,163,184,0.9)"
                                  textAnchor="middle"
                                  transform={`rotate(-90 14 ${(pad + (h - pad)) / 2})`}
                                >
                                  Valeur
                                </text>

                                {/* Series */}
                                <path
                                  d={d}
                                  fill="none"
                                  stroke="rgb(34, 211, 238)"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />

                                {/* Hover crosshair + tooltip */}
                                {hoverPt && hoverX !== null && hoverY !== null && (
                                  <g>
                                    <line
                                      x1={hoverX}
                                      x2={hoverX}
                                      y1={pad}
                                      y2={h - pad}
                                      stroke="rgba(34,211,238,0.35)"
                                      strokeWidth="1"
                                    />
                                    <circle cx={hoverX} cy={hoverY} r={4} fill="rgb(34,211,238)" stroke="rgba(15,23,42,0.9)" strokeWidth="2" />

                                    {(() => {
                                      const boxW = 210;
                                      const boxH = 44;
                                      const margin = 10;
                                      const bx = Math.min(Math.max(leftPad, hoverX - boxW / 2), w - pad - boxW);
                                      const by = Math.max(pad, hoverY - boxH - margin);
                                      return (
                                        <g>
                                          <rect
                                            x={bx}
                                            y={by}
                                            width={boxW}
                                            height={boxH}
                                            rx={8}
                                            fill="rgba(15, 23, 42, 0.92)"
                                            stroke="rgba(148,163,184,0.25)"
                                          />
                                          <text x={bx + 10} y={by + 17} fontSize="11" fill="rgba(226,232,240,0.95)">
                                            {hoverLabelX}
                                          </text>
                                          <text x={bx + 10} y={by + 34} fontSize="11" fill="rgba(226,232,240,0.95)">
                                            v = {hoverLabelY}
                                          </text>
                                        </g>
                                      );
                                    })()}
                                  </g>
                                )}
                              </svg>
                            );
                          })()
                        ) : (
                          <div className="text-sm text-slate-500 dark:text-slate-400">Aucune donnée sur la période.</div>
                        )}
                      </div>
                    )}

                    {roomAttrTimeseriesQuery.data?.query && (
                      <div className="mt-3">
                        <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                          Requête envoyée à InfluxDB
                        </div>
                        <pre className="bg-slate-50 dark:bg-[#1E293B] p-3 rounded border border-slate-200 dark:border-gray-700 overflow-auto text-xs dark:text-gray-300 whitespace-pre-wrap font-mono">
                          {roomAttrTimeseriesQuery.data.query}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'heatmap' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">Projet *</label>
                    <select
                      value={selectedProjectId ?? ''}
                      onChange={(e) => setSelectedProjectId(Number(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                      required
                    >
                      {(projectsQuery.data?.data ?? []).length === 0 && <option value="">Aucun projet</option>}
                      {(projectsQuery.data?.data ?? []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title} (ID: {p.id})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">Appartement (optionnel)</label>
                    <input
                      value={heatmapApartmentQuery}
                      onChange={(e) => {
                        setHeatmapApartmentQuery(e.target.value);
                        setHeatmapApartmentId(null);
                        setIsHeatmapApartmentOpen(true);
                      }}
                      onFocus={() => setIsHeatmapApartmentOpen(true)}
                      placeholder="Rechercher un appart…"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                      disabled={allRoomsQuery.isLoading || allRoomsQuery.isError}
                    />
                    {heatmapApartmentId !== null && (
                      <button
                        type="button"
                        onClick={() => {
                          setHeatmapApartmentId(null);
                          setHeatmapApartmentQuery('');
                        }}
                        className="absolute right-2 top-9 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                        title="Effacer"
                      >
                        <span className="material-icons-outlined text-base">close</span>
                      </button>
                    )}

                    {isHeatmapApartmentOpen && (
                      <div className="absolute z-20 mt-2 w-full bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto">
                        {allRoomsQuery.isLoading && (
                          <div className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">Chargement…</div>
                        )}
                        {allRoomsQuery.isError && (
                          <div className="px-3 py-2 text-sm text-rose-600 dark:text-rose-400">
                            Impossible de charger la liste des appartements.
                          </div>
                        )}
                        {!allRoomsQuery.isLoading && heatmapApartmentResults.length === 0 && (
                          <div className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">Aucun résultat.</div>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            setHeatmapApartmentId(null);
                            setHeatmapApartmentQuery('');
                            setIsHeatmapApartmentOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-colors"
                        >
                          <div className="text-sm font-medium text-slate-900 dark:text-white">Tous les appartements</div>
                        </button>

                        {heatmapApartmentResults.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => {
                              setHeatmapApartmentId(a.id);
                              setHeatmapApartmentQuery(a.name ?? String(a.id));
                              setIsHeatmapApartmentOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-colors"
                          >
                            <div className="text-sm font-medium text-slate-900 dark:text-white">
                            {a.name}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {a.number ? `N° ${a.number} · ` : ''}
                              {a.code ? `Code ${a.code}` : ''}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">metric (optionnel)</label>
                    <select
                      value={heatmapMetric}
                      onChange={(e) => {
                        setHeatmapMetric(e.target.value);
                        // Reset variant when metric changes to avoid invalid combos
                        setHeatmapVariant('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                      disabled={coreAttributesForHeatmapFiltersQuery.isLoading || coreAttributesForHeatmapFiltersQuery.isError}
                    >
                      <option value="">Tous</option>
                      {heatmapMetricOptions.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                    {coreAttributesForHeatmapFiltersQuery.isLoading && (
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Chargement des metrics…</div>
                    )}
                    {coreAttributesForHeatmapFiltersQuery.isError && (
                      <div className="mt-1 text-xs text-rose-600 dark:text-rose-400">Impossible de charger les metrics.</div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-300">variant (optionnel)</label>
                    <select
                      value={heatmapVariant}
                      onChange={(e) => setHeatmapVariant(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                      disabled={
                        coreAttributesForHeatmapFiltersQuery.isLoading ||
                        coreAttributesForHeatmapFiltersQuery.isError ||
                        heatmapVariantOptions.length === 0
                      }
                    >
                      <option value="">Tous</option>
                      {heatmapVariantOptions.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-4 w-4 rounded border bg-rose-500/80 border-rose-600/40" />
                    <span className="text-slate-600 dark:text-slate-300">0</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-4 w-4 rounded border bg-amber-400/80 border-amber-500/40" />
                    <span className="text-slate-600 dark:text-slate-300">1–49</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-4 w-4 rounded border bg-emerald-500/80 border-emerald-600/40" />
                    <span className="text-slate-600 dark:text-slate-300">≥ 50</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Rechercher une chambre</label>
                  <input
                    value={heatmapRoomSearch}
                    onChange={(e) => setHeatmapRoomSearch(e.target.value)}
                    placeholder="Filtrer par nom ou id…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
                  />
                </div>

                {(allRoomsQuery.isLoading || roomsHeatmapQuery.isLoading) && (
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <svg
                      className="animate-spin h-4 w-4 text-slate-500 dark:text-slate-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>
                      {allRoomsQuery.isLoading
                        ? 'Chargement des chambres…'
                        : roomsHeatmapQuery.isLoading
                          ? 'Calcul de la heatmap…'
                          : 'Chargement…'}
                    </span>
                  </div>
                )}
                {(allRoomsQuery.isError || roomsHeatmapQuery.isError) && (
                  <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg p-4">
                    <div className="text-rose-700 dark:text-rose-200 font-semibold">Erreur</div>
                    <div className="text-sm text-rose-700 dark:text-rose-200 mt-1">
                      Impossible de charger la heatmap (rooms/attributes/influx).
                    </div>
                  </div>
                )}

                {!!selectedProjectId && allRoomsQuery.data && (allRoomsQuery.data.rooms?.length ?? 0) === 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <div className="text-amber-800 dark:text-amber-200 font-semibold">Aucune room trouvée</div>
                    <div className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                      Projet #{selectedProjectId}: aucune zone de type <code className="font-mono">ROOM</code> n’a été trouvée via{' '}
                      <code className="font-mono">/projects/{'{projectId}'}/areas/tree</code> (parcours bâtiment → (étage) → appartement → room).
                    </div>
                  </div>
                )}

                {roomsHeatmapQuery.data && (
                  <div className="border border-slate-200 dark:border-[#1E293B] rounded-lg p-4">
                    {debouncedHeatmapRoomSearch.trim() !== '' && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                        Filtre: <span className="font-mono">{debouncedHeatmapRoomSearch.trim()}</span>
                      </div>
                    )}
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                      {roomsHeatmapQuery.data.rooms.length} room(s) • {roomsHeatmapQuery.data.days[0]} →{' '}
                      {roomsHeatmapQuery.data.days[roomsHeatmapQuery.data.days.length - 1]}
                    </div>

                    <div className="space-y-3">
                      {roomsHeatmapQuery.data.rooms
                        .filter((r) => {
                          if (heatmapApartmentId !== null && r.apartmentId !== heatmapApartmentId) return false;
                          const term = debouncedHeatmapRoomSearch.trim().toLowerCase();
                          if (!term) return true;
                          const name = (r.roomName ?? '').toLowerCase();
                          const id = String(r.roomId);
                          return name.includes(term) || id.includes(term);
                        })
                        .map((r) => (
                        <div key={r.roomId} className="py-2 border-b border-slate-200 dark:border-[#1E293B]">
                          <div className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                            {r.roomName}
                          </div>
                          <div className="flex gap-1 flex-wrap">
                            {roomsHeatmapQuery.data!.days.map((d, i) => (
                              <div
                                key={d}
                                className={`h-5 w-5 rounded border ${heatmapColorClass(r.counts[i] ?? 0)}`}
                                title={`${d}: ${r.counts[i] ?? 0} point(s)`}
                                aria-label={`${d}: ${r.counts[i] ?? 0} point(s)`}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!!selectedProjectId &&
                  allRoomsQuery.data &&
                  (allRoomsQuery.data.rooms?.length ?? 0) > 0 &&
                  !roomsHeatmapQuery.data &&
                  !roomsHeatmapQuery.isLoading && (
                  <div className="bg-slate-50 dark:bg-[#0B1116] border border-slate-200 dark:border-[#1E293B] rounded-lg p-4 text-sm text-slate-700 dark:text-slate-300">
                    Rooms trouvées: <b>{allRoomsQuery.data.rooms?.length ?? 0}</b>. (En attente du calcul heatmap…)
                  </div>
                )}

                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Note: pour l’instant, on calcule les counts en “buckettant” les points retournés par `/influx/v1/timeseries` (peut être lourd si beaucoup de rooms).
                </div>

                {roomsHeatmapQuery.data?.query && (
                  <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="text-sm font-semibold mb-2 dark:text-white">Flux query utilisée</div>
                    <pre className="bg-gray-50 dark:bg-[#1E293B] p-3 rounded border border-gray-200 dark:border-gray-700 overflow-auto text-xs dark:text-gray-300 whitespace-pre-wrap font-mono">
                      {roomsHeatmapQuery.data.query}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-800 dark:text-red-200 font-medium">Error</p>
          <pre className="mt-2 text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap overflow-auto">
            {error}
          </pre>
        </div>
      )}

      {activeTab === 'timeseries' && timeseriesDebugQuery && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 dark:text-white">Generated Flux Query</h2>
          <pre className="bg-gray-50 dark:bg-[#1E293B] p-4 rounded border border-gray-200 dark:border-gray-700 overflow-auto text-sm dark:text-gray-300 whitespace-pre-wrap font-mono">
            {timeseriesDebugQuery}
          </pre>
        </div>
      )}

      {activeTab === 'timeseries' && timeseriesResult && (
        <div className="bg-white dark:bg-[#16202A] rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 dark:text-white">Result</h2>
          <pre className="bg-gray-50 dark:bg-[#1E293B] p-4 rounded border border-gray-200 dark:border-gray-700 overflow-auto text-sm dark:text-gray-300">
            {JSON.stringify({ ...timeseriesResult, query: undefined }, null, 2)}
          </pre>
        </div>
      )}
        </div>
      </div>
    </main>
    </div>
  );
}
